import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@folio/components';
import { cn } from '@folio/lib';
import type { EmailAccountDto } from 'korint-sdk';
import { useEmailAccountControllerReconnect } from 'korint-sdk';
import { MoreHorizontal, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Text } from '@folio/components/ui/text';
import { EMAIL_ACCOUNT_STATUS_DOT, emailAccountStatusTextClassName } from '../emailAccountStatus';
import { ProviderLogo } from './ProviderLogo';

export function AccountCard({
  account,
  canManageSharedAccounts,
  onDelete,
}: {
  account: EmailAccountDto;
  canManageSharedAccounts: boolean;
  onDelete: (account: EmailAccountDto) => void;
}) {
  const { t } = useTranslation();
  const canManage = account.isShared ? canManageSharedAccounts : account.isOwn;
  const canReconnect = canManage && account.status === 'error';

  const { mutateAsync: reconnect } = useEmailAccountControllerReconnect();
  const [isReconnecting, setIsReconnecting] = useState(false);

  const handleReconnect = async () => {
    setIsReconnecting(true);
    try {
      const { url } = await reconnect({
        emailAccountId: account.id,
        data: { returnUrl: window.location.origin + window.location.pathname },
      });
      // Keep the spinner up: the full-page redirect unloads the component, so never reset here.
      window.location.assign(url);
    } catch {
      setIsReconnecting(false);
    }
  };

  return (
    <li
      className={cn(
        'flex items-start gap-3 rounded-lg border bg-card p-3',
        account.status === 'error' && 'border-destructive/30 bg-destructive/5',
      )}
    >
      <ProviderLogo type={account.emailProviderType} />
      <div className="min-w-0 flex-1">
        <Text truncate size="sm" weight="semibold">
          {account.primaryEmailAddress}
        </Text>
        <Text
          size="xs"
          className={cn(
            'flex items-center gap-1.5',
            emailAccountStatusTextClassName(account.status),
          )}
        >
          <span
            aria-hidden
            className={cn(
              'size-1.5 shrink-0 rounded-full',
              EMAIL_ACCOUNT_STATUS_DOT[account.status],
            )}
          />
          {t(`connectedEmailAccounts.status.${account.status}`)}
          {canReconnect && (
            <>
              <span aria-hidden>·</span>
              <Button
                variant="link"
                size="xs"
                loading={isReconnecting}
                disabled={isReconnecting}
                onClick={handleReconnect}
                className="h-auto p-0 text-xs font-semibold text-destructive underline"
              >
                {t('connectedEmailAccounts.reconnect')}
              </Button>
            </>
          )}
        </Text>
        {account.isShared && (
          <Text truncate size="xs" tone="muted">
            {t('connectedEmailAccounts.connectedBy', { name: account.connectedBy })}
          </Text>
        )}
      </div>
      {canManage && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="xs"
              aria-label={t('connectedEmailAccounts.actions', {
                email: account.primaryEmailAddress,
              })}
              className="size-7 shrink-0 p-0"
            >
              <MoreHorizontal className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onSelect={() => onDelete(account)}
              className="gap-2 text-destructive focus:text-destructive"
            >
              <Trash2 className="size-4" aria-hidden />
              {t('connectedEmailAccounts.delete.action')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </li>
  );
}
